// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.0
// updated  : 2026-06-21
// protected: NO  -> UAT-02 Fase 2: editor baris PENGAJUAN (Kacab) TANPA QR.
//            Dipakai Transfer/Pengiriman/Compliment. Output: request_lines.
// =============================================================================
import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";

const SIZES = ["S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL", "6XL", "7XL"];
const rid = () => Math.random().toString(36).slice(2, 9);
const newRow = () => ({ key: rid(), product_id: "", item_category: "Jual", sizes: {}, qtyNoSize: "" });

// Membangun request_lines (flatten) dari builder rows.
const buildLines = (rows, productById) => {
  const out = [];
  for (const r of rows) {
    if (!r.product_id) continue;
    const p = productById(r.product_id);
    const cat = r.item_category || "Jual";
    const base = { product_id: r.product_id, product_name: p?.name || null, club: p?.club || null, item_category: cat };
    if (p && !p.has_size_variants) {
      const q = Number(r.qtyNoSize || 0);
      if (q > 0) out.push({ ...base, size: "-", qty: q });
    } else {
      for (const sz of SIZES) {
        const q = Number(r.sizes[sz] || 0);
        if (q > 0) out.push({ ...base, size: sz, qty: q });
      }
    }
  }
  return out;
};

export const RequestLineEditor = ({ products = [], allowMou = true, onLinesChange, testid = "req-editor", initialRows = null }) => {
  const [rows, setRows] = useState(() => (initialRows && initialRows.length ? initialRows : [newRow()]));
  const productById = useCallback((id) => products.find((p) => p.id === id), [products]);

  useEffect(() => {
    onLinesChange?.(buildLines(rows, productById));
  }, [rows, productById, onLinesChange]);

  const setRow = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeRow = (key) => setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.key !== key) : rs));
  const setSizeQty = (key, sz, val) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, sizes: { ...r.sizes, [sz]: val } } : r)));

  const rowTotal = (r) => {
    const p = productById(r.product_id);
    if (p && !p.has_size_variants) return Number(r.qtyNoSize || 0);
    return SIZES.reduce((s, sz) => s + Number(r.sizes[sz] || 0), 0);
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      {rows.map((r, idx) => {
        const p = productById(r.product_id);
        return (
          <div key={r.key} className="rounded-xl border p-3 space-y-3" data-testid={`${testid}-row`}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className={allowMou ? "md:col-span-8" : "md:col-span-11"}>
                <Label className="text-xs">Merchandize</Label>
                <Select value={r.product_id} onValueChange={(v) => setRow(r.key, { product_id: v, sizes: {}, qtyNoSize: "" })}>
                  <SelectTrigger data-testid={`${testid}-merch`}><SelectValue placeholder="Pilih Merchandize..." /></SelectTrigger>
                  <SelectContent>
                    {products.map((pr) => (
                      <SelectItem key={pr.id} value={pr.id} data-testid={`${testid}-merch-${pr.id}`}>
                        {pr.name} — {pr.club}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {allowMou && (
                <div className="md:col-span-3">
                  <Label className="text-xs">Jenis Stok</Label>
                  <Select value={r.item_category} onValueChange={(v) => setRow(r.key, { item_category: v })}>
                    <SelectTrigger data-testid={`${testid}-jenis`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Jual" data-testid={`${testid}-jenis-jual`}>JUAL</SelectItem>
                      <SelectItem value="MoU" data-testid={`${testid}-jenis-mou`}>MoU</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="md:col-span-1 flex justify-end">
                <Button variant="ghost" size="icon" className="text-destructive" onClick={() => removeRow(r.key)}
                  disabled={rows.length <= 1} data-testid={`${testid}-remove`} title="Hapus baris">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {r.product_id && (
              p && !p.has_size_variants ? (
                <div className="max-w-[200px]">
                  <Label className="text-xs">Quantity</Label>
                  <Input type="number" min="0" value={r.qtyNoSize}
                    onChange={(e) => setRow(r.key, { qtyNoSize: e.target.value })}
                    placeholder="0" data-testid={`${testid}-qty-nosize`} />
                </div>
              ) : (
                <div>
                  <Label className="text-xs">Size &amp; Quantity</Label>
                  <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 mt-1">
                    {SIZES.map((sz) => (
                      <div key={sz}>
                        <div className="text-[10px] text-center text-muted-foreground">{sz}</div>
                        <Input type="number" min="0" value={r.sizes[sz] || ""}
                          onChange={(e) => setSizeQty(r.key, sz, e.target.value)}
                          className="text-center px-1" placeholder="0" data-testid={`${testid}-size-${sz}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
            <div className="text-xs text-muted-foreground">Total baris #{idx + 1}: <b>{rowTotal(r)}</b> pcs</div>
          </div>
        );
      })}
      <Button variant="outline" size="sm" onClick={() => setRows((rs) => [...rs, newRow()])} data-testid={`${testid}-add`}>
        <Plus className="w-4 h-4 mr-1" />Tambah Merchandize
      </Button>
    </div>
  );
};

// Ringkasan baris request untuk tampilan list/dialog.
export const RequestLinesSummary = ({ lines = [], testid = "req-summary" }) => {
  if (!lines.length) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1" data-testid={testid}>
      {lines.map((l, i) => (
        <span key={i} className="text-[11px] rounded-md bg-muted px-1.5 py-0.5" data-testid={`${testid}-item`}>
          {l.product_name || l.club || "?"} · {l.size} · {l.qty}x {l.item_category === "MoU" ? "[MoU]" : ""}
        </span>
      ))}
    </div>
  );
};
